import React from "react";

/**
 * Template #5 — zoom_frame (3D Perspective)
 * Zoom into complexity without losing context.
 * Parts: frame
 * Actions: zoom_in, zoom_out, move
 *
 * 3D perspective frame with depth edges, corner brackets, and zoom indicator.
 */

export interface ZoomFrameProps {
  cx?: number;
  cy?: number;
  frameWidth?: number;
  frameHeight?: number;
  zoomLevel?: number;
  contextFaded?: boolean;
  label?: string;
  opacity?: number;
  accentColor?: string;
  children?: React.ReactNode;
}

export const ZoomFrame: React.FC<ZoomFrameProps> = ({
  cx = 400,
  cy = 200,
  frameWidth = 200,
  frameHeight = 150,
  zoomLevel = 1,
  contextFaded = false,
  label,
  opacity = 1,
  accentColor = "#3b82f6",
  children,
}) => {
  const x = cx - frameWidth / 2;
  const y = cy - frameHeight / 2;
  const d = 6; // 3D depth offset
  const scale = 1 + (zoomLevel - 1) * 0.5;

  const uid = `zf-${Math.round(cx)}-${Math.round(cy)}`;

  return (
    <g opacity={opacity}>
      {/* Context fade */}
      {contextFaded && (
        <>
          <defs>
            <mask id={`${uid}-mask`}>
              <rect x="0" y="0" width="800" height="450" fill="white" />
              <rect x={x - 4} y={y - 4} width={frameWidth + 8} height={frameHeight + 8} rx={8} fill="black" />
            </mask>
          </defs>
          <rect x="0" y="0" width="800" height="450" fill="#0f172a" opacity={0.45} mask={`url(#${uid}-mask)`} />
        </>
      )}

      {/* 3D frame shadow (offset) */}
      <rect x={x + d} y={y + d} width={frameWidth} height={frameHeight} rx={8} fill="#000" opacity={0.15} />

      {/* Frame fill */}
      <rect x={x} y={y} width={frameWidth} height={frameHeight} rx={8} fill="#0f172a" opacity={0.3} />

      {/* Frame border */}
      <rect x={x} y={y} width={frameWidth} height={frameHeight} rx={8} fill="none" stroke={accentColor} strokeWidth={2} opacity={0.6} />
      {/* Inner highlight */}
      <rect x={x + 2} y={y + 2} width={frameWidth - 4} height={frameHeight - 4} rx={6} fill="none" stroke="#fff" strokeWidth={0.5} opacity={0.05} />

      {/* 3D corner brackets (raised) */}
      {[
        { px: x, py: y, dx: 20, dy: 20 },
        { px: x + frameWidth, py: y, dx: -20, dy: 20 },
        { px: x, py: y + frameHeight, dx: 20, dy: -20 },
        { px: x + frameWidth, py: y + frameHeight, dx: -20, dy: -20 },
      ].map((c, i) => (
        <g key={i}>
          {/* Shadow */}
          <line x1={c.px + 1} y1={c.py + 1} x2={c.px + c.dx + 1} y2={c.py + 1} stroke="#000" strokeWidth={3} opacity={0.2} strokeLinecap="round" />
          <line x1={c.px + 1} y1={c.py + 1} x2={c.px + 1} y2={c.py + c.dy + 1} stroke="#000" strokeWidth={3} opacity={0.2} strokeLinecap="round" />
          {/* Bracket lines */}
          <line x1={c.px} y1={c.py} x2={c.px + c.dx} y2={c.py} stroke={accentColor} strokeWidth={3} strokeLinecap="round" />
          <line x1={c.px} y1={c.py} x2={c.px} y2={c.py + c.dy} stroke={accentColor} strokeWidth={3} strokeLinecap="round" />
          {/* Specular */}
          <line x1={c.px} y1={c.py} x2={c.px + c.dx * 0.6} y2={c.py} stroke="#fff" strokeWidth={1} opacity={0.15} strokeLinecap="round" />
        </g>
      ))}

      {/* Zoom indicator pill */}
      {zoomLevel > 1 && (
        <g transform={`translate(${x + frameWidth - 32}, ${y + 10})`}>
          <rect x={0} y={0} width={28} height={22} rx={11} fill="#0f172a" stroke={accentColor} strokeWidth={1.2} />
          <rect x={1} y={1} width={26} height={3} rx={2} fill="#fff" opacity={0.04} />
          <text x={14} y={15} fill={accentColor} textAnchor="middle" fontSize={10} fontWeight={700} fontFamily="'Inter', system-ui, sans-serif">
            {zoomLevel.toFixed(1)}×
          </text>
        </g>
      )}

      {/* Zoomed content */}
      <g transform={`translate(${cx}, ${cy}) scale(${scale}) translate(${-cx}, ${-cy})`}>
        <clipPath id={`${uid}-clip`}>
          <rect x={x + 4} y={y + 4} width={frameWidth - 8} height={frameHeight - 8} rx={4} />
        </clipPath>
        <g clipPath={`url(#${uid}-clip)`}>{children}</g>
      </g>

      {/* Label */}
      {label && (
        <text x={cx} y={y + frameHeight + 24} fill="#94a3b8" textAnchor="middle" fontSize={12} fontWeight={500} fontFamily="'Inter', system-ui, sans-serif">
          {label}
        </text>
      )}
    </g>
  );
};
